import { useState, useMemo } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useI18n } from '../../../hooks/useI18n';
import { Document, DocumentCategoryEnum } from "@syadem/kairos-pro-js";

interface DocumentListProps {
  documents: Document[];
  onDelete: (documentId: string) => void;
  onEdit: (documentId: string) => void;
}

type Column = {
  id: string;
  label: string;
  width: string;
  sortable?: boolean;
};

function DocumentList({ documents, onDelete, onEdit }: DocumentListProps) {
  const { t } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<string>("createdAt");

  const columns: Column[] = [
    { id: 'name', label: t('documents.attributes.name'), width: '30%' },
    { id: 'category', label: t('documents.attributes.category'), width: '20%' },
    { id: 'attachments', label: t('documents.attributes.attachments'), width: '15%', sortable: false },
    { id: 'date', label: t('documents.attributes.date'), width: '15%' },
    { id: 'createdAt', label: t('documents.attributes.createdAt'), width: '15%' },
    { id: 'actions', label: '', width: '5%', sortable: false },
  ];

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedDocuments = useMemo(() => {
    const getValueFn = (document: Document): string => {
      switch (orderBy) {
        case "name":
          return document.name;
        case "category":
          return document.category;
        case "createdAt":
          return document.createdAt.toISOString();
        default:
          return "";
      }
    };

    const sortFn = (a: Document, b: Document) => {
      if (orderBy === "date") {
        if (!a.date) return 1;
        if (!b.date) return -1;
        return order === "asc" ? a.date.getTime() - b.date.getTime() : b.date.getTime() - a.date.getTime();
      }

      const aValue = getValueFn(a);
      const bValue = getValueFn(b);
      return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    };

    return [...documents].sort(sortFn);
  }, [documents, order, orderBy]);

  const getCategoryChipStyling = (category: string) => {
    if (category === DocumentCategoryEnum.VaccinationBook) {
      return {
        backgroundColor: theme.palette.success[100],
        border: `1px solid ${theme.palette.primary[200]}`,
        color: theme.palette.success[700],
      };
    }

    return {
      backgroundColor: theme.palette.neutral[100],
      border: `1px solid ${theme.palette.neutral[300]}`,
      color: theme.palette.neutral[800],
    };
  };

  if (isMobile) {
    return (
      <Box sx={{ mx: 2 }}>
        {sortedDocuments.map((document) => (
          <Card
            key={document.id}
            elevation={0}
            sx={{
              mb: 2,
              border: `1px solid ${theme.palette.neutral[200]}`,
              '&:hover': {
                borderColor: theme.palette.primary[200],
                cursor: 'pointer'
              }
            }}
            onClick={() => onEdit(document.id)}
          >
            <CardContent sx={{ px: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    maxWidth: "70%"
                  }}
                >
                  {document.name}
                </Typography>
                <Chip
                  label={t(`documents.attributes.categories.${document.category}`)}
                  size="medium"
                  sx={{
                    ml: 1,
                    px: 0.5,
                    fontSize: '0.75rem',
                    '& .MuiChip-label': {
                      px: 1
                    },
                    ...getCategoryChipStyling(document.category)
                  }}
                />
              </Stack>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('documents.created_at', { date: document.createdAt.toLocaleDateString() })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachFileIcon sx={{ fontSize: '1rem', mr: 0.5, color: theme.palette.neutral[500] }} />
                  <Typography variant="body2" color="text.secondary">
                    {`${document.attachments.length} ${document.attachments.length === 1 ? t('documents.file') : t('documents.files')}`}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(document.id);
                  }}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none'
                  }}
                >
                  {t('common.cta.delete')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{
      border: `solid 1px ${theme.palette.neutral[200]}`,
      borderRadius: 1,
      overflow: 'hidden',
    }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{
              backgroundColor: theme.palette.background.default,
              borderBottom: `2px solid ${theme.palette.primary[100]}`,
            }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.id === 'actions' || column.id === 'attachments' ? 'center' : 'left'}
                  sx={{
                    width: column.width,
                    fontWeight: 600,
                    padding: '14px 12px',
                  }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDocuments.map((document) => (
              <TableRow
                key={document.id}
                hover
                sx={{
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor: theme.palette.neutral[100],
                  },
                  borderBottom: `solid 1px ${theme.palette.neutral[200]}`
                }}
                onClick={() => onEdit(document.id)}
              >
                <TableCell sx={{ padding: '12px' }}>
                  <Box sx={{ display: "flex", alignItems: "center", maxWidth: "100%" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 250
                      }}
                    >
                      {document.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Chip
                    label={t(`documents.attributes.categories.${document.category}`)}
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      ...getCategoryChipStyling(document.category)
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ padding: '12px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AttachFileIcon sx={{ fontSize: '1rem', mr: 0.5, color: theme.palette.neutral[600] }} />
                    <Typography variant="body2">
                      {document.attachments.length}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {document.date?.toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px' }}>
                  <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {document.createdAt.toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ padding: '12px' }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(document.id);
                    }}
                    size="small"
                    color="error"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default DocumentList;
